import { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ images, index, onClose, onChange }) {
    const prev = useCallback(() => {
        onChange((index - 1 + images.length) % images.length);
    }, [index, images.length, onChange]);

    const next = useCallback(() => {
        onChange((index + 1) % images.length);
    }, [index, images.length, onChange]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose, prev, next]);

    if (!images || images.length === 0 || index == null) return null;

    return (
        <div
            data-testid="lightbox"
            className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm grain"
            onClick={onClose}
        >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-phosphor-amber">
                    &gt; ARCHIVE_VIEW &nbsp;·&nbsp; PHOTO {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
                </div>
                <button
                    data-testid="lightbox-close"
                    onClick={onClose}
                    className="inline-flex items-center gap-2 border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/80 hover:border-phosphor-amber hover:text-phosphor-amber transition-colors"
                >
                    <X className="h-3 w-3" /> Close
                </button>
            </div>

            {/* Main frame */}
            <div
                className="relative grid place-items-center h-[calc(100vh-64px)] px-16"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="scanlines border border-border max-h-full max-w-full">
                    <img
                        data-testid="lightbox-img"
                        src={images[index]}
                        alt=""
                        className="max-h-[80vh] max-w-[85vw] object-contain"
                    />
                </div>

                {images.length > 1 && (
                    <>
                        <button
                            data-testid="lightbox-prev"
                            onClick={prev}
                            className="absolute left-6 top-1/2 -translate-y-1/2 border border-border bg-background/80 p-3 hover:border-phosphor-amber hover:text-phosphor-amber transition-colors"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                            data-testid="lightbox-next"
                            onClick={next}
                            className="absolute right-6 top-1/2 -translate-y-1/2 border border-border bg-background/80 p-3 hover:border-phosphor-amber hover:text-phosphor-amber transition-colors"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
